import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  /*
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  */
  TextField,
  /* Tooltip, */
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  TablePagination
} from '@mui/material';
import {
  /*
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  */
  Business as BusinessIcon,
  Sync as SyncIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import companyService from '../services/companyService';

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Pagination & Filter State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  /*
  // Dialog State (disabled)
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCompany, setCurrentCompany] = useState({
    displayName: '',
    address: '',
    phone: '',
  });

  // Delete Dialog State (disabled)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  */

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCompanies();
  }, [page, rowsPerPage, searchTerm]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        sortBy: "createdAt",
        sortOrder: "desc",
      };
      const response = await companyService.getAllCompanies(params);

      if (Array.isArray(response)) {
        setCompanies(response);
        setTotalCount(response.length);
        return;
      }

      const companiesData = response?.companys || [];
      setCompanies(companiesData);
      setTotalCount(
        response?.pagination?.total ?? response?.stats?.total ?? companiesData.length,
      );
    } catch (error) {
      console.error('Fetch error:', error);
      showSnackbar('Error fetching companies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await companyService.syncFromDataverse();
      showSnackbar('Sync from Dataverse completed successfully', 'success');
      fetchCompanies();
    } catch (error) {
      console.error('Sync error:', error);
      showSnackbar('Error syncing from Dataverse', 'error');
    } finally {
      setSyncing(false);
    }
  };

  /*
  // Create/Update/Delete handlers (disabled)
  const handleOpenDialog = (company = null) => {
    if (company) {
      setIsEditing(true);
      setCurrentCompany({
        id: company.id,
        displayName: company.displayName || company.name,
        address: company.address || '',
        phone: company.phone || '',
      });
    } else {
      setIsEditing(false);
      setCurrentCompany({ displayName: '', address: '', phone: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCompany({ displayName: '', address: '', phone: '' });
  };

  const handleSubmit = async () => {
    if (!currentCompany.displayName) {
      showSnackbar('Company name is required', 'warning');
      return;
    }

    try {
      const payload = {
        displayName: currentCompany.displayName,
        address: currentCompany.address,
        phone: currentCompany.phone,
      };

      if (isEditing) {
        await companyService.updateCompany(currentCompany.id, payload);
        showSnackbar('Company updated successfully', 'success');
      } else {
        await companyService.createCompany(payload);
        showSnackbar('Company created successfully', 'success');
      }
      fetchCompanies();
      handleCloseDialog();
    } catch (error) {
      console.error('Submit error:', error);
      showSnackbar(`Error ${isEditing ? 'updating' : 'creating'} company`, 'error');
    }
  };

  const handleDeleteClick = (company) => {
    setCompanyToDelete(company);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await companyService.deleteCompany(companyToDelete.id);
      showSnackbar('Company deleted successfully', 'success');
      fetchCompanies();
    } catch (error) {
      console.error('Delete error:', error);
      showSnackbar('Error deleting company', 'error');
    } finally {
      setOpenDeleteDialog(false);
      setCompanyToDelete(null);
    }
  };
  */

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon fontSize="large" color="primary" />
          Company Management
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCompanies}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing || loading}
          >
            Sync Dataverse
          </Button>
          {/*
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Company
          </Button>
          */}
        </Stack>
      </Box>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <TextField
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Created At</TableCell>
                {/*
                <TableCell align="right">Actions</TableCell>
                */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No companies found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {company.displayName || company.name}
                    </TableCell>
                    <TableCell>{company.phone || '-'}</TableCell>
                    <TableCell>{company.address || '-'}</TableCell>
                    <TableCell>{new Date(company.createdAt).toLocaleDateString()}</TableCell>
                    {/*
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(company)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(company)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
      </Paper>

      {/*
      // Create/Edit Dialog (disabled)
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Company' : 'Create New Company'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Company Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentCompany.displayName}
            onChange={(e) =>
              setCurrentCompany({ ...currentCompany, displayName: e.target.value })
            }
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            label="Phone"
            type="text"
            fullWidth
            variant="outlined"
            value={currentCompany.phone}
            onChange={(e) => setCurrentCompany({ ...currentCompany, phone: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Address"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={currentCompany.address}
            onChange={(e) =>
              setCurrentCompany({ ...currentCompany, address: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      */}

      {/*
      // Delete Confirmation Dialog (disabled)
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete company{' '}
            <strong>{companyToDelete?.displayName || companyToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      */}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CompanyManagement;
